import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import Head from 'next/head';
import Layout from '@components/VideoScriptGenerator/Layout';
import Wizard from '@components/VideoScriptGenerator/Wizard';
import MuiProvider from '@context/muiContext';
import Segment from '@components/segment.io';
import { SnackbarProvider } from 'notistack';


export interface EnvConfig {
  defaultOrigin: string;
  recaptchaSiteKey: string;
  recaptchaSiteKeyChallenge: string;
  apiV2Url: string;
  facebookAPI: string;
}

interface ScriptGeneratorProps {
  env: EnvConfig;
}

const ScriptGenerator = ({ env }: ScriptGeneratorProps) => {
  const title = "Sendspark | AI Video Script Generator for Sales"
  const url = `${env.defaultOrigin}/video-script-generator`
  const description = "Let our AI Video Script Generator create video scripts for your sales process today. Use for prospecting, follow ups, demos, and more!"
  const image = `${env.defaultOrigin}/assets/video-script-generator-ai.jpeg`
  const facebookAppId = env.facebookAPI;

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={env.recaptchaSiteKey}
    >
      <div style={{ height: '100vh' }}>
        <Layout env={env}>
          <Head>
            <title>Sendspark | AI Generated Scripts</title>
            <meta name="description" content={description} />
            <link rel="shortcut icon" type="image/png" href={`${env.defaultOrigin}/assets/favicon/favicon.png`} />
            <meta property="og:site_name" content={title} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image" content={image} />
            <meta property="og:image:secure_url" content={image} />
            <meta property="og:image:alt" content={title} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:creator" content="@sendspark" />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:domain" content={url} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:image:alt" content={title} />
            <meta name="twitter:site" content="@sendspark" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:url" content={url} />
            <meta property="fb:app_id" content={facebookAppId} />
            <style>
              {`
                .grecaptcha-badge {
                  visibility: hidden;
                }
                `}
            </style>
          </Head>
          <Wizard env={env} />
        </Layout>
      </div>
    </GoogleReCaptchaProvider>
  )
};

ScriptGenerator.getLayout = (page: React.ReactElement, pageProps: any) => {
  return (
    <MuiProvider>
      <SnackbarProvider style={{ fontSize: 16, justifyContent: 'center' }}>
        <Segment segmentApiKey={pageProps.segmentApiKey} />
        {page}
      </SnackbarProvider>
    </MuiProvider>
  )
}

export async function getServerSideProps() {
  return {
    props: {
      segmentApiKey: process.env.NEXT_PUBLIC_SEGMENT_API_KEY || '',
      env: {
        apiV2Url: process.env.NEXT_PUBLIC_API_V2_URL || '',
        defaultOrigin: process.env.NEXT_PUBLIC_DEFAULT_ORIGIN || '',
        recaptchaSiteKey: process.env.NEXT_PUBLIC_RECAPTCHA_APP_SITE_KEY || '',
        recaptchaSiteKeyChallenge: process.env.NEXT_PUBLIC_RECAPTCHA_SCRIPT_GENERATOR_SITE_KEY_CHALLENGE || '',
        facebookAPI: process.env.FACEBOOK_APP_ID || ''
      },
    },
  }
}

export default ScriptGenerator;
